import React, { useCallback } from 'react';

import { Spin } from 'antd';
import { createCn } from 'bem-react-classname';
import cn from 'classnames';
import { useFormError } from 'hooks';
import { toInteger } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useLifecycles } from 'react-use';
import { server, AppThunk } from 'store';
import { Kit } from 'ui/Kit';
import { toasts } from 'ui/toasts';

import { ProjectInfo } from '../../shared/ProjectInfo';
import { ProjectVersionForm } from '../../shared/ProjectVersionForm';
import { ProjectsMenu } from '../../shared/ProjectsMenu';

import './EditProjectVersionPage.scss';

const cx = createCn('EditProjectVersionPage');

interface Props {}

export const EditProjectVersionPage: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const params = useParams<{ id: string; projectId: string }>();
  const id = toInteger(params.id);
  const projectId = toInteger(params.projectId);

  const refresh = useCallback(async () => {
    dispatch(
      server.getProjectById.thunk.request({
        params: { id: projectId },
      }),
    );
    dispatch(
      server.getProjectVersionById.thunk.request({
        params: { id },
      }),
    );
  }, [dispatch, id, projectId]);

  useLifecycles(
    () => {
      refresh();
    },
    () => {
      dispatch(server.getProjectById.action.reset());
      dispatch(server.getProjectVersionById.action.reset());
      dispatch(server.updateProjectVersionById.action.reset());
    },
  );

  const getProjectByIdState = useSelector(server.getProjectById.selector.state);

  const getProjectVersionByIdState = useSelector(
    server.getProjectVersionById.selector.state,
  );

  const updateProjectVersionByIdState = useSelector(
    server.updateProjectVersionById.selector.state,
  );
  const formError = useFormError(updateProjectVersionByIdState.error);

  const onFinish = useCallback(
    (values) => {
      dispatch(
        ((): AppThunk => async (dispatch, getState) => {
          await dispatch(
            server.updateProjectVersionById.thunk.request({
              params: { id },
              body: values,
            }),
          );
          const { error } = server.updateProjectVersionById.selector.state(
            getState(),
          );

          toasts.result({
            title: 'Save project version',
            error,
          });

          if (!error) {
            refresh();
          }
        })(),
      );
    },
    [dispatch, id, refresh],
  );

  const aggFetching =
    updateProjectVersionByIdState.isFetching ||
    getProjectVersionByIdState.isFetching;
  const { data } = getProjectVersionByIdState;

  return (
    <div className={cn(cx())}>
      <ProjectsMenu />
      <Kit.Title>
        Project version #{id} / edit{' '}
        {aggFetching && <Spin size="small" className="ant-header-spin" />}
      </Kit.Title>

      <Kit.LoadContainer
        error={getProjectVersionByIdState.error}
        isFetched={getProjectVersionByIdState.isFetched}
        isFetching={getProjectVersionByIdState.isFetching}
      >
        <div className={cx('info')}>
          <ProjectInfo
            getProjectByIdState={getProjectByIdState}
            isInfo={false}
          />
        </div>

        <ProjectVersionForm
          onFinish={onFinish}
          formError={formError}
          disabled={aggFetching}
          action={'edit'}
          initialValues={{
            name: data?.version.name || '',
            param: data?.version.param || '',
            localeIds: data?.locales.map((locale) => locale.id) || [],
            projectId,
          }}
          options={{ canChangeParam: data?.canChangeParam }}
        />
      </Kit.LoadContainer>
    </div>
  );
};
